/**
 * Declarar la misma atención en varios días de la semana.
 *
 * POR QUE ESTE DIALOGO
 * ====================
 * Antes había que crear una franja por día: un profesional de lunes a
 * viernes, mañana y tarde, en dos modalidades, eran veinte formularios
 * idénticos salvo el día. Nadie piensa su agenda así.
 *
 * Aquí se marcan los días y se declara una vez. El servidor sigue guardando
 * una fila por día, que es lo correcto porque cada día puede tener horario
 * distinto; lo que deja de hacerse a mano es la traducción.
 *
 * SI SE PISA, SE DICE CON QUE
 * ===========================
 * El servidor rechaza el solapamiento y responde con qué se pisa y qué día.
 * Ese mensaje se muestra tal cual: es más útil que "error al guardar", y lo
 * sabe el servidor, no esta pantalla.
 */

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { crearSemana } from '@/application/appointments/appointment.service'
import { scheduleKeys } from '@/application/appointments/useAppointments'
import { getErrorMessage } from '@/lib/api'

/** Lunes primero: es como se lee una semana de trabajo. */
const DIAS = [
  { numero: 1, nombre: 'Lun' },
  { numero: 2, nombre: 'Mar' },
  { numero: 3, nombre: 'Mié' },
  { numero: 4, nombre: 'Jue' },
  { numero: 5, nombre: 'Vie' },
  { numero: 6, nombre: 'Sáb' },
  { numero: 0, nombre: 'Dom' },
] as const

/** Los turnos que de verdad se usan. Lo demás se escribe a mano. */
const ATAJOS = [
  { etiqueta: 'Lunes a viernes', dias: [1, 2, 3, 4, 5] },
  { etiqueta: 'Toda la semana', dias: [1, 2, 3, 4, 5, 6, 0] },
  { etiqueta: 'Solo sábados', dias: [6] },
] as const

interface Props {
  abierto: boolean
  onCerrar: () => void
  onCreada: () => void
  providerId: string
  nombreDelProfesional: string
  modalidades: readonly { value: string; label: string }[]
  modalidadPorDefecto?: string
  diasIniciales: number[]
  horaInicial?: string
}

export function DeclararLaSemana({
  abierto,
  onCerrar,
  onCreada,
  providerId,
  nombreDelProfesional,
  modalidades,
  modalidadPorDefecto,
  diasIniciales,
  horaInicial,
}: Props) {
  const queryClient = useQueryClient()
  const [dias, setDias] = useState<number[]>(diasIniciales)
  const [modalityId, setModalityId] = useState(modalidadPorDefecto ?? modalidades[0]?.value ?? '')
  const [inicio, setInicio] = useState(horaInicial ?? '08:00')
  const [fin, setFin] = useState('12:00')
  const [duracion, setDuracion] = useState('30')
  const [simultaneos, setSimultaneos] = useState('1')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const alternar = (dia: number) =>
    setDias((previo) =>
      previo.includes(dia) ? previo.filter((d) => d !== dia) : [...previo, dia]
    )

  const guardar = async () => {
    setError(null)
    setGuardando(true)
    try {
      await crearSemana({
        providerId,
        modalityId,
        dias,
        startTime: inicio,
        endTime: fin,
        slotDurationMinutes: Number(duracion),
        maxConcurrent: Number(simultaneos),
      })
      // Sin esto se declara la semana y el calendario sigue vacío hasta que
      // alguien recarga la página: la pantalla no se entera de lo que acaba
      // de crear.
      await queryClient.invalidateQueries({ queryKey: scheduleKeys.all })
      onCreada()
      onCerrar()
    } catch (e) {
      // El mensaje del servidor dice con qué franja se pisa y qué día. Es más
      // útil que cualquier texto que pudieramos poner aqui, pero hay que
      // sacarlo de la respuesta: `e.message` de axios es "Request failed with
      // status code 409", y eso era lo que se le mostraba al administrador en
      // lugar de "El lunes ya hay atencion de 08:00 a 12:00".
      setError(getErrorMessage(e, 'No se pudo declarar la agenda'))
    } finally {
      setGuardando(false)
    }
  }

  const cupos =
    dias.length > 0 && inicio < fin && Number(duracion) > 0
      ? Math.floor(
          ((Number(fin.slice(0, 2)) * 60 + Number(fin.slice(3))) -
            (Number(inicio.slice(0, 2)) * 60 + Number(inicio.slice(3)))) /
            Number(duracion)
        ) * Number(simultaneos)
      : 0

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Declarar atención</DialogTitle>
          <DialogDescription>
            La agenda de {nombreDelProfesional}. Marque los días a los que aplica el mismo
            horario.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm">Días</p>
            <div className="flex flex-wrap gap-1.5">
              {DIAS.map((dia) => {
                const activo = dias.includes(dia.numero)
                return (
                  <button
                    key={dia.numero}
                    type="button"
                    aria-pressed={activo}
                    onClick={() => alternar(dia.numero)}
                    className={`min-w-[44px] rounded-md border px-3 py-1.5 text-sm transition-colors ${
                      activo
                        ? 'border-[var(--primary)] bg-[var(--primary)] text-white'
                        : 'border-[var(--border)] text-[var(--foreground-muted)]'
                    }`}
                  >
                    {dia.nombre}
                  </button>
                )
              })}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {ATAJOS.map((atajo) => (
                <button
                  key={atajo.etiqueta}
                  type="button"
                  onClick={() => setDias([...atajo.dias])}
                  className="text-xs text-[var(--primary)] underline hover:no-underline"
                >
                  {atajo.etiqueta}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm" htmlFor="semana-modalidad">
              Modalidad
            </label>
            <Select
              id="semana-modalidad"
              options={modalidades as { value: string; label: string }[]}
              value={modalityId}
              onChange={(e) => setModalityId(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm" htmlFor="semana-inicio">
                Desde
              </label>
              <Input
                id="semana-inicio"
                type="time"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm" htmlFor="semana-fin">
                Hasta
              </label>
              <Input
                id="semana-fin"
                type="time"
                value={fin}
                onChange={(e) => setFin(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm" htmlFor="semana-duracion">
                Cada cita dura
              </label>
              <Select
                id="semana-duracion"
                options={[10, 15, 20, 30, 45, 60].map((m) => ({
                  value: String(m),
                  label: `${m} minutos`,
                }))}
                value={duracion}
                onChange={(e) => setDuracion(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm" htmlFor="semana-simultaneos">
                Pacientes a la vez
              </label>
              <Input
                id="semana-simultaneos"
                type="number"
                min={1}
                max={50}
                value={simultaneos}
                onChange={(e) => setSimultaneos(e.target.value)}
              />
            </div>
          </div>

          {/* Lo que se va a producir, antes de guardar. Es la pregunta que
              uno tiene: "¿y cuántos pacientes caben?". */}
          {cupos > 0 && (
            <p className="rounded-md bg-[var(--background-secondary)] p-3 text-sm">
              {dias.length === 1 ? (
                <>
                  Quedarán <strong>{cupos} citas</strong> en el día marcado.
                </>
              ) : (
                <>
                  Quedarán <strong>{cupos} citas por día</strong> en cada uno de
                  los {dias.length} días marcados.
                </>
              )}
            </p>
          )}

          {error && (
            <p className="rounded-md border border-[var(--danger)] p-3 text-sm text-[var(--danger)]">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            onClick={() => void guardar()}
            disabled={guardando || dias.length === 0 || !modalityId || inicio >= fin}
          >
            {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Declarar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
